// Admin-Shell für Sprint B. Inhalte (Benutzerverwaltung, Abteilungs-Mgmt,
// Audit-Log, System-Stats, Backup-Restore) kommen schrittweise in den
// nachfolgenden Sprints. Vorerst rendert jede Section einen Placeholder
// damit die Routing-Hooks schon stehen.

import { useLocation } from "wouter-preact";
import { Users, Building2, ScrollText, Activity, Database, Plus, Trash2, Check, X, GitBranch, LayoutGrid, Sparkles } from "lucide-preact";
import { TrashSection } from "../components/admin/TrashSection";
import { Layout } from "../components/Layout";
import { PageShell } from "../components/ui/PageShell";
import { ViewTabs, type ViewTabItem } from "../components/ui/ViewTabs";
import { EmptyState } from "../components/ui/EmptyState";
import { LoadingSkeleton } from "../components/LoadingSkeleton";
import { usePageHeader } from "../lib/pageHeader";
import { requireLabel } from "../lib/navigation";
import { useMe } from "../lib/auth";
import { useEffect, useState } from "preact/hooks";
import {
  useDepartments,
  createDepartment,
  renameDepartment,
  deleteDepartment,
  listDepartmentMembers,
  addDepartmentMember,
  removeDepartmentMember,
  type DepartmentMember,
} from "../store/departments";
import { toastError, toastSuccess } from "../lib/toast";
import { api } from "../lib/api";
import { AiSettingsSection, AiSettingsIcon } from "../components/AiSettingsSection";
import { EmailSettingsSection, EmailSettingsIcon } from "../components/EmailSettingsSection";
import { UsersSection } from "../components/UsersSection";
import { GitAdminSection } from "../components/admin/GitAdminSection";
import { WhatsNewAdminSection } from "../components/admin/WhatsNewAdminSection";
import { AdminSpacesSection } from "../components/admin/AdminSpacesSection";
import { AuditLogSection } from "../components/admin/AuditLogSection";

import { Button } from "../components/ui/Button";

interface AdminProps {
  section?: string;
}

const SECTIONS = [
  { id: "users", label: "Benutzer", icon: Users },
  { id: "departments", label: "Abteilungen", icon: Building2 },
  { id: "spaces", label: "Spaces", icon: LayoutGrid },
  { id: "ai", label: "KI-Einstellungen", icon: AiSettingsIcon },
  { id: "email", label: "E-Mail", icon: EmailSettingsIcon },
  { id: "git", label: "Git", icon: GitBranch },
  { id: "whats-new", label: "Was ist neu", icon: Sparkles },
  { id: "audit", label: "Audit-Log", icon: ScrollText },
  { id: "trash", label: "Papierkorb", icon: Trash2 },
  { id: "stats", label: "System-Stats", icon: Activity },
  { id: "backups", label: "Backups", icon: Database },
] as const;

type SectionId = (typeof SECTIONS)[number]["id"];

export function Admin({ section }: AdminProps): preact.JSX.Element {
  const me = useMe();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!me.loading && (!me.user || !me.user.is_admin)) {
      setLocation("/dashboard");
    }
  }, [me.loading, me.user, setLocation]);

  const active: SectionId = (SECTIONS.find((s) => s.id === section)?.id ??
    "users") as SectionId;

  const tabItems: ViewTabItem[] = SECTIONS.map((s) => ({
    id: s.id,
    label: s.label,
    icon: s.icon,
  }));

  // Waehrend des Auth-Checks (oder fuer Nicht-Admins vor dem Redirect) keine
  // Sektions-Tabs zeigen — nur den Titel, damit vor dem Redirect nichts
  // aufblitzt, das der Nutzer gleich nicht mehr sehen darf.
  const confirmedAdmin = !me.loading && !!me.user?.is_admin;
  usePageHeader({
    title: requireLabel("/admin"),
    tabs: confirmedAdmin ? (
      <ViewTabs
        items={tabItems}
        active={active}
        onChange={(id) => setLocation(`/admin/${id}`)}
        label="Admin-Bereiche"
      />
    ) : undefined,
  });

  if (me.loading) {
    return (
      <Layout>
        <PageShell width="default">
          <LoadingSkeleton height={64} />
        </PageShell>
      </Layout>
    );
  }
  if (!me.user || !me.user.is_admin) return <></>;

  return (
    <Layout>
      {/* default (1440px): Sektionen tragen Tabellen/Raster (Nutzer, Audit-Log,
          Papierkorb …) — hatte vorher gar kein `max-w`. */}
      <PageShell width="default">
        <AdminSection id={active} />
      </PageShell>
    </Layout>
  );
}

function AdminSection({ id }: { id: SectionId }): preact.JSX.Element {
  if (id === "users") return <UsersSection />;
  if (id === "departments") return <AdminDepartments />;
  if (id === "spaces") return <AdminSpacesSection />;
  if (id === "ai") return <AiSettingsSection />;
  if (id === "email") return <EmailSettingsSection />;
  if (id === "git") return <GitAdminSection />;
  if (id === "whats-new") return <WhatsNewAdminSection />;
  if (id === "audit") return <AuditLogSection />;
  if (id === "trash") return <TrashSection />;
  const meta = SECTIONS.find((s) => s.id === id);
  return (
    <EmptyState
      icon={meta?.icon}
      title={`${meta?.label} folgt`}
      description="Diese Sektion ist noch nicht aktiv. Bei Bedarf können wir sie als nächste Iteration priorisieren — sag Bescheid."
    />
  );
}

interface AdminUserLocal {
  id: number;
  email: string;
  name: string;
  is_admin: boolean;
  is_active: boolean;
  is_external: boolean;
}

function AdminDepartments(): preact.JSX.Element {
  const depts = useDepartments();
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [confirmDelId, setConfirmDelId] = useState<number | null>(null);

  // Member panel state
  const [membersExpandedId, setMembersExpandedId] = useState<number | null>(null);
  const [membersData, setMembersData] = useState<Record<number, DepartmentMember[]>>({});
  const [membersLoading, setMembersLoading] = useState<Record<number, boolean>>({});

  // All admin users — fetched once for add-member picker
  const [allUsers, setAllUsers] = useState<AdminUserLocal[]>([]);
  useEffect(() => {
    api<AdminUserLocal[]>("/api/admin/users")
      .then((all) => setAllUsers(all.filter((u) => u.is_active && !u.is_external)))
      .catch(() => {
        // Without this list the "Mitarbeiter hinzufügen"-Picker stays silently
        // empty — that looked like "no users exist" rather than a load error.
        toastError("Mitarbeiterliste konnte nicht geladen werden.");
      });
  }, []);

  // Per-dept add-member picker state: deptId -> selected userId
  const [addMemberState, setAddMemberState] = useState<Record<number, string>>({});

  // Confirm-remove member: `${deptId}-${userId}`
  const [confirmRemoveMember, setConfirmRemoveMember] = useState<string | null>(null);

  const loadMembers = async (deptId: number): Promise<void> => {
    setMembersLoading((prev) => ({ ...prev, [deptId]: true }));
    try {
      const members = await listDepartmentMembers(deptId);
      setMembersData((prev) => ({ ...prev, [deptId]: members }));
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Mitglieder laden fehlgeschlagen");
    } finally {
      setMembersLoading((prev) => ({ ...prev, [deptId]: false }));
    }
  };

  const toggleMembersPanel = async (deptId: number): Promise<void> => {
    if (membersExpandedId === deptId) {
      setMembersExpandedId(null);
    } else {
      setMembersExpandedId(deptId);
      await loadMembers(deptId);
    }
  };

  const handleAddMember = async (deptId: number): Promise<void> => {
    const userId = addMemberState[deptId];
    if (!userId) return;
    try {
      await addDepartmentMember(deptId, Number(userId));
      setAddMemberState((prev) => ({ ...prev, [deptId]: "" }));
      await loadMembers(deptId);
      toastSuccess("Mitarbeiter hinzugefügt.");
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Hinzufügen fehlgeschlagen");
    }
  };

  const handleRemoveMember = async (
    deptId: number,
    userId: number,
    memberName: string
  ): Promise<void> => {
    try {
      await removeDepartmentMember(deptId, userId);
      setConfirmRemoveMember(null);
      await loadMembers(deptId);
      toastSuccess(`${memberName} entfernt.`);
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Entfernen fehlgeschlagen");
    }
  };

  const submitNew = async (): Promise<void> => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await createDepartment(name);
      setNewName("");
      toastSuccess(`Abteilung „${name}" angelegt.`);
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Anlegen fehlgeschlagen");
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (id: number, current: string): void => {
    setEditingId(id);
    setEditName(current);
  };

  const submitEdit = async (id: number): Promise<void> => {
    const name = editName.trim();
    if (!name) return;
    try {
      await renameDepartment(id, name);
      setEditingId(null);
      toastSuccess("Abteilung umbenannt.");
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Umbenennen fehlgeschlagen");
    }
  };

  const submitDelete = async (id: number, name: string): Promise<void> => {
    try {
      await deleteDepartment(id);
      setConfirmDelId(null);
      toastSuccess(`Abteilung „${name}" gelöscht.`);
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Löschen fehlgeschlagen");
    }
  };

  return (
    <section class="space-y-4">
      <div class="rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-4">
        <h3 class="text-sm font-medium text-[color:var(--color-on-surface)] mb-2">
          Neue Abteilung
        </h3>
        <div class="flex gap-2">
          <input
            type="text"
            value={newName}
            onInput={(e) => setNewName((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void submitNew();
              }
            }}
            placeholder="z.B. Entwicklung"
            class="flex-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            aria-label="Name der neuen Abteilung"
          />
          <Button
            variant="primary"
            size="lg"
            type="button"
            onClick={() => void submitNew()}
            disabled={!newName.trim() || creating}
          >
            <Plus size={14} aria-hidden="true" />
            Hinzufügen
          </Button>
        </div>
      </div>

      <div class="rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]">
        {depts.loading ? (
          <p class="p-4 text-sm text-[color:var(--color-on-surface-variant)]">Lädt…</p>
        ) : depts.items.length === 0 ? (
          <p class="p-4 text-sm text-[color:var(--color-on-surface-variant)] italic">
            Noch keine Abteilungen.
          </p>
        ) : (
          <ul class="divide-y divide-[color:var(--color-outline-variant)]">
            {depts.items.map((d) => {
              const membersOpen = membersExpandedId === d.id;
              const members = membersData[d.id] ?? [];
              const isLoadingMembers = membersLoading[d.id] ?? false;
              const selectedUserId = addMemberState[d.id] ?? "";
              const availableUsers = allUsers.filter(
                (u) => !members.some((m) => m.user_id === u.id)
              );
              return (
                <li key={d.id}>
                  <div class="flex flex-wrap items-center gap-3 gap-y-1 px-4 py-2.5 text-sm">
                    <Building2
                      size={14}
                      class={d.is_system ? "text-[color:var(--color-primary)]" : "text-[color:var(--color-on-surface-variant)]"}
                      aria-hidden="true"
                    />
                    {editingId === d.id ? (
                      <>
                        <input
                          type="text"
                          value={editName}
                          onInput={(e) => setEditName((e.target as HTMLInputElement).value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              void submitEdit(d.id);
                            }
                            if (e.key === "Escape") setEditingId(null);
                          }}
                          class="flex-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => void submitEdit(d.id)}
                          class="rounded p-1.5 text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)]"
                          aria-label="Bestätigen"
                        >
                          <Check size={14} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingId(null)}
                          class="rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]"
                          aria-label="Abbrechen"
                        >
                          <X size={14} aria-hidden="true" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span class="flex-1 text-[color:var(--color-on-surface)]">
                          {d.name}
                          {d.is_system && (
                            <span class="ml-2 text-label uppercase tracking-wider text-[color:var(--color-primary)]">
                              System
                            </span>
                          )}
                        </span>

                        {/* Mitglieder toggle — always shown */}
                        <button
                          type="button"
                          onClick={() => void toggleMembersPanel(d.id)}
                          class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] hover:text-[color:var(--color-on-surface)] transition-colors"
                          aria-expanded={membersOpen}
                          aria-label={`Mitglieder von ${d.name} ${membersOpen ? "zuklappen" : "aufklappen"}`}
                        >
                          <Users size={14} aria-hidden="true" />
                          Mitglieder
                        </button>

                        {!d.is_system && (
                          <>
                            <button
                              type="button"
                              onClick={() => startEdit(d.id, d.name)}
                              class="text-xs text-[color:var(--color-primary)] hover:underline"
                            >
                              Umbenennen
                            </button>
                            {confirmDelId === d.id ? (
                              <>
                                <Button
                                  variant="destructive"
                                  size="xs"
                                  type="button"
                                  onClick={() => void submitDelete(d.id, d.name)}
                                >
                                  Wirklich löschen
                                </Button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmDelId(null)}
                                  class="rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)]"
                                >
                                  Abbrechen
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setConfirmDelId(d.id)}
                                class="rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] transition-colors"
                                aria-label={`${d.name} löschen`}
                              >
                                <Trash2 size={14} aria-hidden="true" />
                              </button>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>

                  {/* Members panel */}
                  {membersOpen && (
                    <div class="border-t border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container)] px-4 py-3 space-y-2">
                      <p class="text-xs font-medium text-[color:var(--color-on-surface-variant)]">
                        Mitglieder
                      </p>

                      {d.is_system && (
                        <p class="text-xs italic text-[color:var(--color-on-surface-variant)]">
                          Automatisch: alle aktiven internen Nutzer. Die
                          Mitgliedschaft wird nicht manuell gepflegt.
                        </p>
                      )}

                      {isLoadingMembers ? (
                        <p class="text-xs text-[color:var(--color-on-surface-variant)]">Lädt…</p>
                      ) : members.length === 0 ? (
                        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
                          Keine Mitglieder zugewiesen.
                        </p>
                      ) : (
                        <ul class="space-y-1.5">
                          {members.map((m) => {
                            const removeKey = `${d.id}-${m.user_id}`;
                            return (
                              <li key={m.user_id} class="flex items-center gap-2 text-sm">
                                <span class="flex-1 min-w-0">
                                  <span class="text-[color:var(--color-on-surface)]">
                                    {m.name}
                                  </span>
                                  <span class="ml-1 text-xs text-[color:var(--color-on-surface-variant)]">
                                    {m.email}
                                  </span>
                                </span>
                                {d.is_system ? null : confirmRemoveMember ===
                                  removeKey ? (
                                  <>
                                    <Button
                                      variant="destructive"
                                      size="xs"
                                      type="button"
                                      onClick={() =>
                                        void handleRemoveMember(d.id, m.user_id, m.name)
                                      }
                                    >
                                      Wirklich entfernen
                                    </Button>
                                    <button
                                      type="button"
                                      onClick={() => setConfirmRemoveMember(null)}
                                      class="rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)]"
                                    >
                                      Abbrechen
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => setConfirmRemoveMember(removeKey)}
                                    class="rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] transition-colors"
                                    aria-label={`${m.name} aus Abteilung entfernen`}
                                  >
                                    <Trash2 size={14} aria-hidden="true" />
                                  </button>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}

                      {/* Add member picker — hidden for the auto-managed system dept */}
                      {!d.is_system && (
                      <div class="flex gap-2 pt-1 border-t border-[color:var(--color-outline-variant)]">
                        <select
                          value={selectedUserId}
                          onChange={(e) =>
                            setAddMemberState((prev) => ({
                              ...prev,
                              [d.id]: (e.target as HTMLSelectElement).value,
                            }))
                          }
                          class="flex-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1.5 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                          aria-label={`Mitarbeiter für ${d.name} auswählen`}
                        >
                          <option value="">Mitarbeiter wählen…</option>
                          {availableUsers.map((u) => (
                            <option key={u.id} value={String(u.id)}>
                              {u.name} ({u.email})
                            </option>
                          ))}
                        </select>
                        <Button
                          variant="primary"
                          size="md"
                          type="button"
                          onClick={() => void handleAddMember(d.id)}
                          disabled={!selectedUserId}
                        >
                          <Plus size={14} aria-hidden="true" />
                          Mitarbeiter hinzufügen
                        </Button>
                      </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p class="text-xs text-[color:var(--color-on-surface-variant)]">
        Abteilungen sind ein persönlicher Anzeige-Filter — sie schränken den
        Zugriff <strong>nicht</strong> ein. Jeder User abonniert in seinen
        Einstellungen die Abteilungen die ihn interessieren.
      </p>
    </section>
  );
}
